import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useModalDecision } from '@providers/booking_continue/use_modal_decision'
import type { ContinueGuard } from '@providers/booking_continue'
import { buildErrorMessage } from 'components-ui/src/shared/error'
import { CustomError } from 'components-ui/src/shared/error_handler'
import { captureException } from 'service-api/src/shared'
import type { CustomInvoiceClientDto } from 'service-api/src/types/api'
import { useUserStatus } from '../../hooks/use_userstatus'
import { fetchVisitConflictingInvoices } from './conflict_check'

type ConflictCheckArgs = {
  period: string[]
  locationPetProfileIds: string[]
}

type ActiveConflictCheck = {
  promise: Promise<boolean>
}

export const useBookingConflictContinueGuard = (locationName: string, args: ConflictCheckArgs) => {
  const userStatus = useUserStatus()
  const decision = useModalDecision()
  const [conflictInvoices, setConflictInvoices] = useState<CustomInvoiceClientDto[]>([])
  const [conflictingPetIds, setConflictingPetIds] = useState<string[]>([])
  // Invalidates an async result when its attempt is cancelled or the hook unmounts before the request settles.
  const generationRef = useRef(0)
  // Reuses the current conflict-check promise so overlapping calls cannot start duplicate requests or modal decisions.
  const activeCheckRef = useRef<ActiveConflictCheck>()

  const close = useCallback(() => {
    generationRef.current += 1
    activeCheckRef.current = undefined
    setConflictInvoices([])
    decision.cancel()
  }, [decision])

  const run = useCallback((): Promise<boolean> => {
    if (activeCheckRef.current) return activeCheckRef.current.promise

    const generation = ++generationRef.current
    const activeCheck: ActiveConflictCheck = { promise: Promise.resolve(false) }
    const promise = (async () => {
      try {
        const results = userStatus.isLoggedIn
          ? await fetchVisitConflictingInvoices(locationName, args.period, args.locationPetProfileIds)
          : []

        if (generation !== generationRef.current) return false
        if (!results.length) return true

        setConflictingPetIds(args.locationPetProfileIds)
        setConflictInvoices(results)
        const result = await decision.requestDecision()
        setConflictInvoices([])
        return result
      } catch (error) {
        if (generation !== generationRef.current) return false

        // Conflict validation intentionally fails open after reporting the request failure.
        captureException(new CustomError(buildErrorMessage(error), 'Conflict booking'))
        return true
      } finally {
        if (activeCheckRef.current === activeCheck) {
          activeCheckRef.current = undefined
        }
      }
    })()

    activeCheck.promise = promise
    activeCheckRef.current = activeCheck
    return promise
  }, [args.locationPetProfileIds, args.period, decision, locationName, userStatus.isLoggedIn])

  useEffect(
    () => () => {
      generationRef.current += 1
      activeCheckRef.current = undefined
    },
    []
  )

  const guard = useMemo<ContinueGuard>(
    () => ({
      id: 'booking-conflict',
      run,
      cancel: close,
    }),
    [close, run]
  )

  return {
    guard,
    modalProps: {
      invoices: conflictInvoices,
      locationPetProfileIds: conflictingPetIds,
      isOpen: decision.isOpen && conflictInvoices.length > 0,
      onClose: close,
      onReviewDates: close,
      onBookAnyway: decision.confirm,
    },
  }
}
